import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet } from 'react-native';
import { View } from 'react-native';
import {
  BodyText,
  Checkbox,
  SectionHeader,
  TextLinkifyTags,
  theme,
  useResponsive,
  VStack,
} from '@sg/garnish';

import { CheckoutLayoutBlock } from '@order/components';
import { useLocalizationContext } from '@order/Localization';

import { SweetpassCheckoutCta } from '..';

export const SweetpassCheckoutBlock = ({
  testID,
  termsAndConditions,
  isLoading,
  hasAcceptedTerms,
  canCheckout,
  handleAcceptTerms,
  handleCta,
}: SweetpassCheckoutBlockProps) => {
  const { t, formatDate, formatPrice } = useLocalizationContext();
  const { currentBreakpoint } = useResponsive();

  const onCheckTerms = React.useCallback(
    (checked?: boolean) => {
      handleAcceptTerms(checked ?? false);
    },
    [handleAcceptTerms],
  );

  // TODO: Fetch subscription cost from the actual plan.
  const price = formatPrice(1000, 'USD');
  const parsedTermsAndConditions = termsAndConditions
    ?.replace('{date}', formatDate(new Date()))
    ?.replace('{price}', price);

  return (
    <CheckoutLayoutBlock testID={testID}>
      <SectionHeader heading={t('general.your-order')} />

      <VStack
        customSpacing={0}
        divider={true}
        dividerColor={theme.colors.LIGHT}
      >
        <View style={styles.lineItem}>
          <BodyText bold>{t('sweetpass-plus')}</BodyText>
          <BodyText>
            {t('sweetpass.checkout.membership-price', { price })}
          </BodyText>
        </View>
      </VStack>

      <VStack customSpacing={theme.spacing['4']} style={styles.topBorder}>
        <Checkbox
          testID="sweetpass.checkout.accept"
          style={styles.checkbox}
          checked={hasAcceptedTerms}
          onChange={onCheckTerms}
          label={parsedTermsAndConditions}
          bold
        />
        {currentBreakpoint.isXS ? null : (
          <>
            <SweetpassCheckoutCta
              testID="sweetpass.checkout.cta-desktop"
              isLoading={isLoading}
              disabled={!canCheckout}
              onPress={handleCta}
            />
            <BodyText style={styles.upgradeNotice} size={5}>
              <TextLinkifyTags
                size={5}
                links={[
                  {
                    tag: 'terms',
                    description: t(
                      'sweetpass.checkout.upgrade-cta-notice.terms-description',
                    ),
                    href: t('sweetpass-upgrade.confirm-modal.terms-url'),
                    newTab: true,
                  },
                ]}
              >
                {t('sweetpass.checkout.upgrade-cta-notice')}
              </TextLinkifyTags>
            </BodyText>
          </>
        )}
      </VStack>
    </CheckoutLayoutBlock>
  );
};

// ─── TYPES ─────────────────────────────────────────────────────────────────────

type SweetpassCheckoutBlockProps = {
  readonly termsAndConditions?: string;
  readonly isLoading: boolean;
  readonly hasAcceptedTerms: boolean;
  readonly canCheckout: boolean;
  readonly handleAcceptTerms: (hasAcceptedTerms: boolean) => void;
  readonly handleCta: () => void;
} & Pick<ViewProps, 'testID'>;

//
// ─── STYLES ─────────────────────────────────────────────────────────────────────
//

const styles = StyleSheet.create({
  topBorder: {
    paddingTop: theme.spacing['6'],
    borderTopWidth: 1,
    borderTopColor: theme.colors.TEXT_COLOR,
  },
  lineItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing['10'],
  },
  checkbox: {
    alignItems: 'flex-start',
  },
  upgradeNotice: {
    paddingTop: theme.spacing['2'],
    textAlign: 'center',
    color: theme.colors.NEUTRAL_2,
  },
});
